import React, {Fragment} from 'react'
import {connect} from 'react-redux'
import styles from './Friends.scss'
import {Card} from 'common/styleguide/Card'
import {ColWrap, MainCol, SecondaryCol} from 'common/layouts/SideNavLayout/TwoCol'
import {IntroWidget} from 'common/IntroWidget'
import {Loadable} from 'common/Loadable'
import {FriendsActivity} from './FriendsActivity'
import {FriendRequestsList} from './FriendRequestsList'
import {urls} from 'lib/urls'
import {ProfileImg} from 'common/ProfileImg'
import {CTA} from 'common/styleguide/CTA'
import {InviteFriendsDialog, INVITE_FRIENDS_DIALOG} from './dialogs/InviteFriendsDialog'
import _ from 'lodash'
import {apiv3} from 'lib/apiv3'
import * as currentUserActions from 'lib/api/currentUser'
import {friendsSelectors} from 'data/reducers/friends'
import {userActions} from 'data/resources/users'
import {DEFAULT_OFFSET, DEFAULT_PER_PAGE} from 'lib/loadAllPages'
import Helmet from 'react-helmet'
import {IeNudgeBar} from 'common/IeNudgeBar'

export class Friends extends React.PureComponent {
  constructor (props) {
    super(props)
    const {loadableResources: {feed}} = this.props

    this.state = {
      dialog: null,
      feed: feed,
      loadingMore: false,
      offset: DEFAULT_OFFSET
    }
  }

  showDialog = () => {
    this.setState({dialog: INVITE_FRIENDS_DIALOG})
  }

  hideDialog = () => {
    this.setState({dialog: null})
  }

  getMoreFriendsActivity = () => {
    const {currentUser} = this.props
    const {offset} = this.state

    apiv3.users.feedItems(currentUser.id, {
      extras: true,
      order_by: 'created_at',
      order_dir: 'desc',
      page: offset / DEFAULT_PER_PAGE,
      perPage: DEFAULT_PER_PAGE
    }).then((response) => {
      this.setState((prevState) => {
        const combinedResults = _.concat(prevState.feed.results, response.results)
        const combinedExtras = _.concat(prevState.feed.extras, response.extras)
        return {
          feed: Object.assign(
            {},
            prevState.feed,
            {results: combinedResults, extras: combinedExtras, totalCount: response.totalCount}
          ),
          loadingMore: false
        }
      })
    })
  }

  loadMore = () => {
    this.setState((state) => ({loadingMore: true, offset: state.offset + DEFAULT_PER_PAGE}), () => this.getMoreFriendsActivity())
  }

  render () {
    const {
      friends,
      pendingIncomingFriends,
      pendingOutgoingFriends,
      confirmFriendRequest,
      declineFriendRequest,
      unfriend,
      currentUser,
      setPreference
    } = this.props
    const {feed, dialog, loadingMore, offset} = this.state

    return (
      <div className={styles.Friends}>
        <Helmet title='Friends · Ride with GPS' />
        <IeNudgeBar currentUser={currentUser} setPreference={setPreference} />
        <ColWrap>
          {dialog === INVITE_FRIENDS_DIALOG && <InviteFriendsDialog onRequestClose={this.hideDialog} />}
          <MainCol>
            <h2 data-heading-alt>Friends Activity</h2>
            <Card noPadding>
              {feed && feed.results.length === 0
                ? <UserHasNoFeedActivity />
                : <FriendsActivity
                  feed={feed}
                  currentUser={currentUser}
                  loadMore={this.loadMore}
                  loadingMore={loadingMore}
                  offset={offset} />}
            </Card>
          </MainCol>
          <SecondaryCol>
            <h2 data-heading-alt>Add Friends</h2>
            <Card>
              <CTA secondary margins={false} onClick={this.showDialog}>Invite Friends</CTA>
              <CTA link margins={false} href={'/users'} className={styles.findFriendsCTA}>Find Friends</CTA>
            </Card>
            {!(pendingIncomingFriends.isEmpty() && pendingOutgoingFriends.isEmpty()) &&
              <FriendRequestsList
                pendingIncomingFriends={pendingIncomingFriends}
                pendingOutgoingFriends={pendingOutgoingFriends}
                onConfirmClicked={userId => confirmFriendRequest(userId)}
                onIgnoreClicked={userId => declineFriendRequest(userId)}
                onUnfriendClicked={userId => unfriend(userId)} />}
            {!friends.isEmpty() && <Fragment>
              <h2 data-heading-alt>Your Friends</h2>
              <Card>
                <FriendsList friends={friends} />
              </Card>
            </Fragment>}
          </SecondaryCol>
        </ColWrap>
      </div>
    )
  }
}

const UserHasNoFeedActivity = () => (
  <IntroWidget large i='md-person-add' link={<a href='/users'>Add Friends</a>}>
    Add friends to see their activity here
  </IntroWidget>
)

export class FriendsList extends React.PureComponent {
  state = {
    isCollapsed: true
  }

  toggleCollapse = () => {
    this.setState({isCollapsed: !this.state.isCollapsed})
  }

  render () {
    const {isCollapsed} = this.state
    const {friends, canCollapse} = this.props
    const toShow = canCollapse
      ? (isCollapsed ? friends.slice(0, 8) : friends)
      : friends

    return <Fragment>
      {toShow.toIndexedSeq().map((user) => {
        return <a href={urls.users.show(user.id)} key={user.id} className={styles.friendListing}>
          <ProfileImg user={user} />
          <span>{user.name}</span>
        </a>
      })}
      {canCollapse && <a data-card-link onClick={this.toggleCollapse}>
        {isCollapsed ? 'Show More' : 'Show Less'}
      </a>}
    </Fragment>
  }
}

const loadInitialResources = _.memoize(({currentUser, loadFriendsForUser, loadFriendRequestsForUser}) => {
  const loadCurrentUserFriends = () => (
    loadFriendsForUser(currentUser.id)
  )

  const loadCurrentUserFriendRequests = () => (
    loadFriendRequestsForUser(currentUser.id)
  )

  const loadCurrentUserInitialFeed = () => (
    apiv3.users.feedItems(currentUser.id, {extras: true, order_by: 'created_at', order_dir: 'desc'})
  )

  const loadAll = Promise.all([
    loadCurrentUserFriends(),
    loadCurrentUserFriendRequests(),
    loadCurrentUserInitialFeed()
  ])

  return loadAll.then(
    ([_friends, _pendingFriends, feed]) => ({
      feed
    })
  )
})

const LoadableFriends = (props) => {
  return <Loadable
    {...props}
    component={Friends}
    loadingText='Loading Friends'
    resourceLoaded={() => loadInitialResources(props)} />
}

const mapDispatchToProps = {
  loadFriendsForUser: userActions.allFriends,
  loadFriendRequestsForUser: userActions.allFriendRequests,
  confirmFriendRequest: userActions.confirmFriendRequest,
  declineFriendRequest: userActions.declineFriendRequest,
  unfriend: userActions.unfriend,
  setPreference: currentUserActions.setPreference
}

const mapStateToProps = state => {
  const {currentUser} = state

  return {
    currentUser: currentUser,
    friends: friendsSelectors.userFriends(state, currentUser.id),
    pendingIncomingFriends: friendsSelectors.userPendingIncomingFriends(state, currentUser.id),
    pendingOutgoingFriends: friendsSelectors.userPendingOutgoingFriends(state, currentUser.id)
  }
}

export const FriendsContainer = connect(
  mapStateToProps,
  mapDispatchToProps
)(LoadableFriends)
